import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Phone, PhoneCall, PhoneOff, PhoneMissed, Mic, MicOff,
  Volume2, Play, Pause, Clock, TrendingUp, Users, Target,
  MessageCircle, RefreshCw, Filter, Search, Loader2, AlertCircle,
  CheckCircle, XCircle, Calendar, BarChart3, Headphones
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CallCenter = ({ token }) => {
  const [calls, setCalls] = useState([]);
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterOutcome, setFilterOutcome] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Active call state
  const [activeCall, setActiveCall] = useState(null);
  const [callTranscript, setCallTranscript] = useState([]);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [callTimer, setCallTimer] = useState(0);
  const timerRef = useRef(null);
  const recognitionRef = useRef(null);
  const audioRef = useRef(null);

  // New call dialog
  const [showNewCall, setShowNewCall] = useState(false);
  const [newCallData, setNewCallData] = useState({
    student_name: '', student_phone: '', student_email: '',
    call_type: 'sales', course_context: '', notes: ''
  });

  // Transcript view dialog
  const [viewingCall, setViewingCall] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [callsRes, configRes] = await Promise.all([
        axios.get(`${API}/calls?limit=100`, { headers, withCredentials: true }),
        axios.get(`${API}/calls/provider/status`, { headers, withCredentials: true })
      ]);
      setCalls(callsRes.data);
      setConfig(configRes.data);
      setStats({ total: callsRes.data.length });
    } catch (err) {
      console.error('Failed to fetch call data:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Call timer
  useEffect(() => {
    if (activeCall) {
      timerRef.current = setInterval(() => setCallTimer(t => t + 1), 1000);
    } else {
      clearInterval(timerRef.current);
      setCallTimer(0);
    }
    return () => clearInterval(timerRef.current);
  }, [activeCall]);

  const formatDuration = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // ==================== BROWSER AI VOICE CALL ====================

  const startBrowserCall = async () => {
    if (!newCallData.student_name) {
      toast.error('Student name is required');
      return;
    }

    try {
      const res = await axios.post(`${API}/calls/browser/start`, newCallData, { headers, withCredentials: true });
      const { call_id, greeting } = res.data;

      setActiveCall({ call_id, ...newCallData });
      setCallTranscript([{ role: 'ai', text: greeting }]);
      setShowNewCall(false);

      // Speak the greeting
      await speakText(greeting);

      // Start listening
      startListening(call_id);
      toast.success('Call started');
    } catch (err) {
      toast.error('Failed to start call');
    }
  };

  const speakText = async (text) => {
    setIsSpeaking(true);
    try {
      const res = await axios.post(`${API}/calls/browser/tts`, { text }, {
        headers, withCredentials: true, responseType: 'blob'
      });
      const audioUrl = URL.createObjectURL(res.data);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      await new Promise((resolve) => {
        audio.onended = resolve;
        audio.onerror = resolve;
        audio.play();
      });
    } catch {
      // Fallback to browser TTS
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-IN';
      utterance.rate = 1.0;
      window.speechSynthesis.speak(utterance);
      await new Promise(resolve => { utterance.onend = resolve; });
    }
    setIsSpeaking(false);
  };

  const startListening = (callId) => {
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      toast.error('Speech recognition not supported in this browser');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-IN';

    recognition.onstart = () => setIsListening(true);

    recognition.onresult = async (event) => {
      const transcript = event.results[0][0].transcript;
      setIsListening(false);

      // Add student message
      setCallTranscript(prev => [...prev, { role: 'student', text: transcript }]);

      // Get AI response
      try {
        const res = await axios.post(`${API}/calls/browser/message`, {
          call_id: callId, text: transcript
        }, { headers, withCredentials: true });

        const aiText = res.data.response;
        setCallTranscript(prev => [...prev, { role: 'ai', text: aiText }]);

        // Speak the response
        await speakText(aiText);

        // Continue listening
        if (activeCall) {
          startListening(callId);
        }
      } catch (err) {
        toast.error('AI response failed');
      }
    };

    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);

    recognitionRef.current = recognition;
    recognition.start();
  };

  const endCall = async (outcome = 'pending') => {
    if (!activeCall) return;

    // Stop listening
    if (recognitionRef.current) recognitionRef.current.stop();
    if (audioRef.current) audioRef.current.pause();
    window.speechSynthesis.cancel();

    try {
      await axios.post(`${API}/calls/browser/end`, {
        call_id: activeCall.call_id,
        outcome,
        duration_seconds: callTimer
      }, { headers, withCredentials: true });

      toast.success('Call ended');
    } catch (err) {
      console.error('Failed to end call:', err);
    }

    setActiveCall(null);
    setCallTranscript([]);
    setIsListening(false);
    setIsSpeaking(false);
    fetchData();
  };

  // ==================== BULK ACTIONS ====================

  const triggerFeeReminders = async () => {
    try {
      const res = await axios.post(`${API}/calls/bulk/fee-reminders`, {}, { headers, withCredentials: true });
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error('Failed to create fee reminders');
    }
  };

  const triggerFollowUps = async () => {
    try {
      const res = await axios.post(`${API}/calls/bulk/admission-followups`, {}, { headers, withCredentials: true });
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error('Failed to create follow-ups');
    }
  };

  const updateCallOutcome = async (callId, outcome) => {
    try {
      await axios.put(`${API}/calls/${callId}`, { outcome }, { headers, withCredentials: true });
      toast.success('Call updated');
      fetchData();
    } catch (err) {
      toast.error('Failed to update call');
    }
  };

  // Play TTS for a transcript message
  const playTTS = async (text) => {
    setIsSpeaking(true);
    try {
      const res = await axios.post(`${API}/calls/browser/tts`, { text }, {
        headers, withCredentials: true, responseType: 'blob'
      });
      const audioUrl = URL.createObjectURL(res.data);
      const audio = new Audio(audioUrl);
      audio.onended = () => setIsSpeaking(false);
      audio.onerror = () => setIsSpeaking(false);
      audio.play();
    } catch {
      // Fallback to browser TTS
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'hi-IN';
      utterance.rate = 1.0;
      utterance.onend = () => setIsSpeaking(false);
      window.speechSynthesis.speak(utterance);
    }
  };

  // Generate demo call
  const generateDemoCall = async () => {
    try {
      toast.loading('Generating demo call with Sia...', { id: 'demo' });
      const res = await axios.post(`${API}/calls/demo`, {
        student_name: 'Rahul',
        scenario: 'bdes_animation',
        call_type: 'sales'
      }, { headers, withCredentials: true });
      toast.success('Demo call generated! Check Call Logs.', { id: 'demo' });
      setViewingCall(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to generate demo', { id: 'demo' });
    }
  };

  // ==================== FILTER ====================
  const filteredCalls = calls.filter(c => {
    if (filterType !== 'all' && c.call_type !== filterType) return false;
    if (filterOutcome !== 'all' && c.outcome !== filterOutcome) return false;
    if (searchQuery && !c.student_name?.toLowerCase().includes(searchQuery.toLowerCase()) && !c.student_phone?.includes(searchQuery)) return false;
    return true;
  });

  const outcomeColors = {
    converted: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    callback_scheduled: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    not_interested: 'bg-red-500/20 text-red-400 border-red-500/30',
    no_answer: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30'
  };

  const typeIcons = {
    sales: <Target className="w-4 h-4" />,
    fee_reminder: <Clock className="w-4 h-4" />,
    follow_up: <RefreshCw className="w-4 h-4" />,
    support: <Headphones className="w-4 h-4" />
  };

  if (loading) return <div className="flex justify-center p-8"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="call-center">
      {/* Active Call Overlay */}
      {activeCall && (
        <Card className="border-2 border-green-500/50 bg-green-500/5" data-testid="active-call-panel">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center animate-pulse">
                  <PhoneCall className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <CardTitle className="text-lg">Live Call: {activeCall.student_name}</CardTitle>
                  <p className="text-sm text-muted-foreground">{activeCall.call_type} call &middot; {formatDuration(callTimer)}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Badge className={isListening ? 'bg-red-500/20 text-red-400 animate-pulse' : 'bg-muted'}>
                  {isListening ? <><Mic className="w-3 h-3 mr-1" /> Listening</> : <><MicOff className="w-3 h-3 mr-1" /> Idle</>}
                </Badge>
                {isSpeaking && <Badge className="bg-blue-500/20 text-blue-400 animate-pulse"><Volume2 className="w-3 h-3 mr-1" /> Speaking</Badge>}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-48 mb-4 rounded-lg bg-background/50 p-3">
              {callTranscript.map((msg, i) => (
                <div key={i} className={`mb-2 flex ${msg.role === 'ai' ? 'justify-start' : 'justify-end'}`}>
                  <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${msg.role === 'ai' ? 'bg-primary/10 text-foreground' : 'bg-blue-500/20 text-blue-300'}`}>
                    <span className="font-medium text-xs opacity-60">{msg.role === 'ai' ? 'Sia (AI)' : 'Student'}</span>
                    <p>{msg.text}</p>
                  </div>
                </div>
              ))}
            </ScrollArea>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={() => { if (!isListening && activeCall) startListening(activeCall.call_id); }}
                disabled={isListening || isSpeaking} data-testid="listen-btn">
                <Mic className="w-4 h-4 mr-1" /> {isListening ? 'Listening...' : 'Listen'}
              </Button>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => endCall('converted')} data-testid="end-converted-btn">
                <CheckCircle className="w-4 h-4 mr-1" /> End - Converted
              </Button>
              <Button size="sm" variant="outline" onClick={() => endCall('callback_scheduled')}>
                <Calendar className="w-4 h-4 mr-1" /> End - Callback
              </Button>
              <Button size="sm" variant="outline" onClick={() => endCall('not_interested')}>
                <XCircle className="w-4 h-4 mr-1" /> End - Not Interested
              </Button>
              <Button size="sm" variant="destructive" onClick={() => endCall('pending')} data-testid="end-call-btn">
                <PhoneOff className="w-4 h-4 mr-1" /> End Call
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="call-stats">
          <Card><CardContent className="p-4 text-center">
            <Phone className="w-5 h-5 mx-auto mb-1 text-blue-400" />
            <p className="text-2xl font-bold">{stats.total_calls}</p>
            <p className="text-xs text-muted-foreground">Total Calls</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <CheckCircle className="w-5 h-5 mx-auto mb-1 text-emerald-400" />
            <p className="text-2xl font-bold">{stats.converted}</p>
            <p className="text-xs text-muted-foreground">Converted</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <TrendingUp className="w-5 h-5 mx-auto mb-1 text-green-400" />
            <p className="text-2xl font-bold">{stats.conversion_rate}%</p>
            <p className="text-xs text-muted-foreground">Conversion Rate</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <Calendar className="w-5 h-5 mx-auto mb-1 text-blue-400" />
            <p className="text-2xl font-bold">{stats.callback_scheduled}</p>
            <p className="text-xs text-muted-foreground">Callbacks</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <Clock className="w-5 h-5 mx-auto mb-1 text-amber-400" />
            <p className="text-2xl font-bold">{formatDuration(stats.avg_duration_seconds)}</p>
            <p className="text-xs text-muted-foreground">Avg Duration</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <BarChart3 className="w-5 h-5 mx-auto mb-1 text-purple-400" />
            <p className="text-2xl font-bold">{stats.recent_7d}</p>
            <p className="text-xs text-muted-foreground">Last 7 Days</p>
          </CardContent></Card>
        </div>
      )}

      {/* Actions Bar */}
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => setShowNewCall(true)} data-testid="new-call-btn">
            <PhoneCall className="w-4 h-4 mr-2" /> New Call
          </Button>
          <Button variant="outline" onClick={triggerFeeReminders} data-testid="fee-reminders-btn">
            <Clock className="w-4 h-4 mr-2" /> Fee Reminders
          </Button>
          <Button variant="outline" onClick={triggerFollowUps} data-testid="followup-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Admission Follow-ups
          </Button>
          <Button variant="outline" className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10" onClick={generateDemoCall} data-testid="demo-call-btn">
            <Play className="w-4 h-4 mr-2" /> Demo Call (Rahul - B.Des)
          </Button>
        </div>
        <div className="flex gap-2 items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search calls..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              className="pl-9 w-48" data-testid="search-calls" />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-36"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="sales">Sales</SelectItem>
              <SelectItem value="fee_reminder">Fee Reminder</SelectItem>
              <SelectItem value="follow_up">Follow-up</SelectItem>
              <SelectItem value="support">Support</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterOutcome} onValueChange={setFilterOutcome}>
            <SelectTrigger className="w-36"><SelectValue placeholder="Outcome" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Outcomes</SelectItem>
              <SelectItem value="converted">Converted</SelectItem>
              <SelectItem value="callback_scheduled">Callback</SelectItem>
              <SelectItem value="not_interested">Not Interested</SelectItem>
              <SelectItem value="no_answer">No Answer</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" size="icon" onClick={fetchData}><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* Exotel Status Banner */}
      {config && !config.ready_for_calls && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <div>
              <p className="font-medium text-sm">Exotel not ready — Sia cannot make calls yet</p>
              <p className="text-xs text-muted-foreground">
                {config.missing?.length
                  ? `Missing: ${config.missing.join(', ')}. Complete KYC + get ExoPhone at exotel.com.`
                  : 'Check Exotel credentials in backend .env'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Call Logs Table */}
      <Card data-testid="call-logs">
        <CardHeader>
          <CardTitle className="text-base">Call Logs ({filteredCalls.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredCalls.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Phone className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No calls yet. Start a new call to begin.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredCalls.map(call => (
                <div key={call.call_id}
                  className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer"
                  onClick={() => setViewingCall(call)} data-testid={`call-row-${call.call_id}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${call.direction === 'inbound' ? 'bg-blue-500/20' : 'bg-green-500/20'}`}>
                      {call.direction === 'inbound' ? <Phone className="w-4 h-4 text-blue-400" /> : <PhoneCall className="w-4 h-4 text-green-400" />}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{call.student_name}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        {typeIcons[call.call_type]}
                        <span className="capitalize">{call.call_type?.replace('_', ' ')}</span>
                        <span>&middot;</span>
                        <span>{call.student_phone || 'No phone'}</span>
                        {call.duration_seconds > 0 && <><span>&middot;</span><span>{formatDuration(call.duration_seconds)}</span></>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`text-xs ${outcomeColors[call.outcome] || outcomeColors.pending}`}>
                      {call.outcome?.replace('_', ' ')}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {new Date(call.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Call Dialog */}
      <Dialog open={showNewCall} onOpenChange={setShowNewCall}>
        <DialogContent className="max-w-md" data-testid="new-call-dialog">
          <DialogHeader><DialogTitle>Start New Call</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Student Name *</Label>
              <Input value={newCallData.student_name} onChange={e => setNewCallData({...newCallData, student_name: e.target.value})}
                placeholder="Enter student name" data-testid="call-student-name" />
            </div>
            <div>
              <Label>Phone Number</Label>
              <Input value={newCallData.student_phone} onChange={e => setNewCallData({...newCallData, student_phone: e.target.value})}
                placeholder="+91XXXXXXXXXX" data-testid="call-phone" />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={newCallData.student_email} onChange={e => setNewCallData({...newCallData, student_email: e.target.value})}
                placeholder="student@email.com" />
            </div>
            <div>
              <Label>Call Type</Label>
              <Select value={newCallData.call_type} onValueChange={v => setNewCallData({...newCallData, call_type: v})}>
                <SelectTrigger data-testid="call-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sales">Sales / New Lead</SelectItem>
                  <SelectItem value="fee_reminder">Fee Reminder</SelectItem>
                  <SelectItem value="follow_up">Follow-up</SelectItem>
                  <SelectItem value="support">Support</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Context / Notes</Label>
              <Textarea value={newCallData.course_context} onChange={e => setNewCallData({...newCallData, course_context: e.target.value})}
                placeholder="Any context for the AI agent..." rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewCall(false)}>Cancel</Button>
            <Button onClick={startBrowserCall} disabled={!newCallData.student_name} data-testid="start-call-btn">
              <Headphones className="w-4 h-4 mr-2" /> Start AI Call
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Call Transcript Dialog */}
      <Dialog open={!!viewingCall} onOpenChange={() => setViewingCall(null)}>
        <DialogContent className="max-w-lg" data-testid="call-transcript-dialog">
          <DialogHeader>
            <DialogTitle>Call: {viewingCall?.student_name}</DialogTitle>
          </DialogHeader>
          {viewingCall && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">Type:</span> <span className="capitalize">{viewingCall.call_type?.replace('_', ' ')}</span></div>
                <div><span className="text-muted-foreground">Direction:</span> <span className="capitalize">{viewingCall.direction}</span></div>
                <div><span className="text-muted-foreground">Phone:</span> {viewingCall.student_phone || 'N/A'}</div>
                <div><span className="text-muted-foreground">Duration:</span> {formatDuration(viewingCall.duration_seconds || 0)}</div>
                <div><span className="text-muted-foreground">Method:</span> <span className="capitalize">{viewingCall.method}</span></div>
                <div><span className="text-muted-foreground">Outcome:</span> <Badge className={`text-xs ${outcomeColors[viewingCall.outcome] || ''}`}>{viewingCall.outcome?.replace('_', ' ')}</Badge></div>
              </div>

              {viewingCall.transcript?.length > 0 && (
                <div>
                  <Label className="mb-2 block">Transcript</Label>
                  <ScrollArea className="h-64 rounded-lg border p-3 bg-muted/30">
                    {viewingCall.transcript.map((msg, i) => (
                      <div key={i} className={`mb-3 ${msg.role === 'ai' ? 'text-left' : 'text-right'}`}>
                        <div className="flex items-center gap-1 mb-0.5">
                          <span className="text-xs font-medium text-muted-foreground">{msg.role === 'ai' ? 'Sia (AI)' : 'Student'}</span>
                          {msg.role === 'ai' && (
                            <button onClick={(e) => { e.stopPropagation(); playTTS(msg.text); }}
                              className="text-xs text-blue-400 hover:text-blue-300 ml-1" disabled={isSpeaking} title="Play audio">
                              <Volume2 className="w-3 h-3 inline" />
                            </button>
                          )}
                        </div>
                        <p className={`text-sm rounded-lg px-3 py-2 inline-block max-w-[85%] ${msg.role === 'ai' ? 'bg-primary/10' : 'bg-blue-500/15'}`}>{msg.text}</p>
                      </div>
                    ))}
                  </ScrollArea>
                </div>
              )}

              {/* Quick outcome update */}
              <div>
                <Label className="mb-2 block">Update Outcome</Label>
                <div className="flex gap-2 flex-wrap">
                  {['converted', 'callback_scheduled', 'not_interested', 'no_answer', 'pending'].map(o => (
                    <Button key={o} size="sm" variant={viewingCall.outcome === o ? 'default' : 'outline'}
                      onClick={() => { updateCallOutcome(viewingCall.call_id, o); setViewingCall({...viewingCall, outcome: o}); }}
                      className="capitalize text-xs">{o.replace('_', ' ')}</Button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CallCenter;
